import { cn } from '@/utils'
import { useMemo, useState } from 'react'
import { useCADQuery } from '@/services/queries/cads'
import { useDndContext } from '@dnd-kit/core'
import { useSelectable } from '@/pages/CADPage/hooks'
import { useIsReadOnly } from '@/pages/CADPage/hooks/useIsReadOnly'
import { Skeleton } from '@/components/ui/skeleton'
import { ProjectDocumentTitle } from '@/components/core/ProjectDocumentTitle'
import { CADVersionDropdown } from '../CADVersionDropdown'
import { DraggableTreeNodeOverlay } from './DraggableTreeNodeOverlay'
import { SubAssemblyTree } from './SubAssemblyTree'
import { HideAllPartsButton } from './HideAllPartsButton'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { ASMTreeNode, useCADPageStore } from '@/state'
import { ToggleAssemblyTreeButton } from '../ToggleAssemblyTreeButton'
import { TreeNodes } from './TreeNodes'
import { AnimatedSearchInput } from './AnimatedSearchInput'

const normalize = (str: string) =>
  str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

export const AssemblyTree = () => {
  const { data: readOnlyData } = useIsReadOnly()
  const isReadOnly = readOnlyData.isReadOnly

  const { isLoading: isCadDataLoading, data: cadData } = useCADQuery()

  const [searchQuery, setSearchQuery] = useState('')
  const tree = useMemo(
    () => cadData?.rawAssemblyTree,
    [cadData?.rawAssemblyTree],
  )

  const selectedPartsCount = useCADPageStore(
    (state) => state.selectedParts.length,
  )

  const subAssemblyDocumentPageId = useCADPageStore(
    (state) => state.subAssemblyDocumentPageId,
  )
  const isInSubAssemblyMode = !!subAssemblyDocumentPageId

  const selectable = useSelectable({})

  const filteredTree = useMemo(() => {
    if (!tree || !searchQuery) return tree

    const nodes = tree?.nodes ?? []
    const idToNode = new Map<string, ASMTreeNode>()

    nodes.forEach((node) => {
      idToNode.set(node.uuid, { ...node })
    })

    const allChildIds = new Set(nodes.flatMap((n) => n.children))
    const rootIds = nodes
      .filter((n) => !allChildIds.has(n.uuid))
      .map((n) => n.uuid)

    const includedIds = new Set<string>()
    const normalizedQuery = normalize(searchQuery)

    function includeAllDescendants(id: string) {
      const stack = [id]
      while (stack.length) {
        const currentId = stack.pop()!
        if (includedIds.has(currentId)) continue
        includedIds.add(currentId)
        const currentNode = idToNode.get(currentId)
        if (currentNode) {
          stack.push(...currentNode.children)
        }
      }
    }

    function shouldIncludeNode(id: string): boolean {
      const node = idToNode.get(id)
      if (!node) return false

      const nameMatches = normalize(node.display_name).includes(normalizedQuery)
      let anyChildMatches = false

      for (const childId of node.children) {
        if (shouldIncludeNode(childId)) {
          anyChildMatches = true
        }
      }

      if (nameMatches) {
        includeAllDescendants(id)
        return true
      } else if (anyChildMatches) {
        includedIds.add(id)
        return true
      }

      return false
    }

    for (const rootId of rootIds) {
      shouldIncludeNode(rootId)
    }

    return {
      ...tree,
      nodes: nodes.filter((n) => includedIds.has(n.uuid)),
    }
  }, [tree, searchQuery])

  const gltf = cadData.gltf

  const isLoading = isCadDataLoading

  const dndContext = useDndContext()
  const isDragging = useMemo(
    () => Boolean(dndContext.activeNode),
    [dndContext.activeNode],
  )

  return (
    <div
      className={cn(
        'bg-white flex flex-col h-full w-full px-2 space-y-2.5 relative',
      )}
    >
      <div className="border rounded-xl flex flex-col flex-grow overflow-hidden border-gray-300 border-2">
        <div className="flex flex-col border-b">
          <div className="flex items-center justify-between px-4 pt-3 pb-2">
            <div className="flex-grow">
              <ProjectDocumentTitle isReadOnly={isReadOnly} />
            </div>
            <ToggleAssemblyTreeButton />
          </div>

          <div className="flex items-center justify-between px-4 pb-3 pt-3 border-t border-gray-200 gap-2">
            <div className="flex items-center gap-2">
              <>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="text-xs font-semibold px-2 py-1 bg-primary-20 text-primary-50 rounded">
                        {selectedPartsCount}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="bg-white text-black border border-gray-200 shadow-md">
                      <p>Parts selected</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                {selectedPartsCount > 0 && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          className="h-6 w-6 p-0 hover:bg-gray-100"
                          onClick={() => {
                            selectable.reset()
                          }}
                        >
                          <XMarkIcon className="w-4 h-4 text-gray-600" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="bg-white text-black border border-gray-200 shadow-md">
                        <p>Clear selected parts</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </>
            </div>
            <div className="flex items-center gap-2 justify-end flex-1">
              <AnimatedSearchInput
                value={searchQuery}
                onChange={setSearchQuery}
              />
              {tree && gltf && (
                <HideAllPartsButton
                  nodes={tree.nodes.filter((node) => node.uuid !== tree.root)}
                  gltf={gltf}
                />
              )}
            </div>
          </div>
        </div>

        <div
          data-testid="assembly-tree"
          className={cn(
            'flex flex-col h-full px-0 py-2 text-sm font-regular pt-0',
            {
              'overflow-y-auto': !isDragging,
              'overflow-y-hidden': isDragging,
            },
          )}
        >
          {isLoading && (
            <div className="flex flex-col space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="w-full h-6 bg-gray-200" />
              ))}
            </div>
          )}
          {!isLoading && filteredTree && gltf && isInSubAssemblyMode && (
            <SubAssemblyTree tree={filteredTree} gltf={gltf} />
          )}

          {!isLoading && filteredTree && gltf && !isInSubAssemblyMode && (
            <TreeNodes tree={filteredTree} gltf={gltf} />
          )}

          <DraggableTreeNodeOverlay />
        </div>
      </div>

      <CADVersionDropdown />
    </div>
  )
}
